/**
 * Consent categories + versioning — single source of truth for the dashboard's
 * first-party consent layer (GDPR_COMPLIANCE_PLAN.md P1-1/P1-2; decision D-1:
 * build a lightweight first-party module, not buy). embed-engine mirrors this
 * shape in its own module.
 *
 * Categories follow the ePrivacy / PECR split: only `necessary` is exempt from
 * prior opt-in. Everything else defaults OFF and turns on ONLY after an
 * explicit, recorded choice (consent-first, fail-closed).
 */

export const CONSENT_COOKIE = 'tc_consent';

// Bump when the category set or policy text materially changes — a version
// mismatch invalidates stored consent and re-prompts (ePrivacy re-consent).
export const CONSENT_VERSION = '2026-06-15.1';

// How long a recorded choice is honored before we re-ask (days).
export const CONSENT_MAX_AGE_DAYS = 180;

// Fired on `window` whenever consent changes so non-React gating code can react.
export const CONSENT_EVENT = 'tc:consent-changed';

export const CATEGORIES = [
  {
    key: 'necessary',
    required: true,
    title: 'Strictly necessary',
    description:
      'Required for the site to work: sign-in, security, multi-tenant routing, and payment fraud-prevention. Always on.',
  },
  {
    key: 'analytics',
    required: false,
    title: 'Analytics',
    description:
      'Product analytics that help us understand how the dashboard is used (PostHog, Vercel, Cloudflare). No session replay.',
  },
  {
    key: 'marketing',
    required: false,
    title: 'Marketing & support chat',
    description:
      'Live chat and marketing tools such as Intercom, Calendly, and product walkthroughs (Supademo).',
  },
  {
    key: 'preferences',
    required: false,
    title: 'Preferences & media',
    description:
      'Embedded media (YouTube, Vimeo, Loom) and other non-essential conveniences that may set cookies.',
  },
];

export const NON_NECESSARY = CATEGORIES.filter((c) => !c.required).map(
  (c) => c.key
);

/** Build a full category map with every non-necessary key set to `granted`. */
export function buildConsent(granted = false) {
  const c = { necessary: true };
  for (const key of NON_NECESSARY) c[key] = !!granted;
  return c;
}

// ── Region rules ────────────────────────────────────────────────────────────
// Visitors in these countries get the opt-in banner (GDPR / UK PECR / Swiss
// FADP: nothing non-essential before an explicit choice). Everyone else is
// opted in by default with a small "Manage" notice (US state-law opt-out
// model), except a browser sending Global Privacy Control, which we treat as
// an opt-out of analytics + marketing (CCPA/CPRA "sale or share").
//
// The country comes from Vercel's `x-vercel-ip-country` header, set into the
// REGION_COOKIE by src/middleware.js. No cookie (local dev, header missing)
// means opt-in — fail closed.
export const REGION_COOKIE = 'tc_region';
export const REGION_OPT_IN = 'optin';
export const REGION_NOTICE = 'notice';

export const OPT_IN_COUNTRIES = new Set([
  // EU
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
  // EEA (non-EU)
  'IS', 'LI', 'NO',
  // UK + Switzerland
  'GB', 'CH',
]);

/** Country code (ISO 3166-1 alpha-2) → REGION_OPT_IN | REGION_NOTICE. */
export function regionForCountry(country) {
  if (!country) return REGION_OPT_IN;
  return OPT_IN_COUNTRIES.has(String(country).toUpperCase())
    ? REGION_OPT_IN
    : REGION_NOTICE;
}

/** Default categories for a notice-region visitor who hasn't chosen yet. */
export function noticeDefaults({ gpc = false } = {}) {
  const c = buildConsent(true);
  if (gpc) {
    c.analytics = false;
    c.marketing = false;
  }
  return c;
}

// Sign-in / sign-up pages use only strictly-necessary cookies, so they show no
// banner or notice and set no default consent; the choice is asked on the
// first page after them.
const AUTH_PATHS = new Set([
  '/login',
  '/magic-login',
  '/demo-login',
  '/forgot-password',
  '/reset-password',
  '/create-account',
  '/session-conflict',
  '/request-access',
]);

// Per-company sign-in pages: /[slug]/login, /[slug]/forgot-password, …
const COMPANY_AUTH_PAGES = new Set(['login', 'forgot-password', 'reset-password']);

export function isAuthPath(pathname) {
  if (!pathname) return false;
  const path = pathname.split(/[?#]/)[0].replace(/\/$/, '') || '/';
  if (AUTH_PATHS.has(path) || path === '/signup' || path.startsWith('/signup/')) {
    return true;
  }
  const parts = path.split('/').filter(Boolean);
  return parts.length === 2 && COMPANY_AUTH_PAGES.has(parts[1]);
}
